import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelopeOpenText, faSpinner, faCopy, faBan, faPlus } from "@fortawesome/free-solid-svg-icons";
import { useAccountType } from "../hooks/useAccountType";
import { ppapiPost, apiErrorMessage } from "../utils/partnerApi";

/**
 * Agency → Invites (KOL_CAMPAIGNS_AGENCY_PLAN.md §7.3 C).
 * An invite link creates a KOL account that is ALREADY linked to the agency
 * (the invite is the consent). Existing accounts that open the link get an
 * Accept / Decline banner instead.
 */

export const INVITE_PARAM = "agencyInvite";
export const inviteUrl = (token) => `${window.location.origin}/login?${INVITE_PARAM}=${encodeURIComponent(token)}`;

const copy = (text) => navigator.clipboard?.writeText(text).then(() => toast.success("Invite link copied")).catch(() => toast.error("Could not copy"));

const AgencyInvites = () => {
  const accountType = useAccountType();
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [label, setLabel] = useState("");
  const [maxUses, setMaxUses] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    ppapiPost("agencyListInvites")
      .then((d) => setInvites(Array.isArray(d?.invites) ? d.invites : []))
      .catch((e) => toast.error(apiErrorMessage(e, "Could not load invites.")))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (accountType === "agency") load(); }, [accountType, load]);

  const create = () => {
    setCreating(true);
    const params = {};
    if (label.trim()) params.label = label.trim().slice(0, 60);
    if (maxUses && Number(maxUses) > 0) params.maxUses = Math.floor(Number(maxUses));
    if (expiresAt) params.expiresAt = `${expiresAt}T23:59:59Z`;
    ppapiPost("agencyCreateInvite", params)
      .then((d) => {
        toast.success("Invite created");
        if (d?.token) copy(inviteUrl(d.token));
        setLabel(""); setMaxUses(""); setExpiresAt("");
        load();
      })
      .catch((e) => toast.error(apiErrorMessage(e, "Could not create the invite.")))
      .finally(() => setCreating(false));
  };

  const revoke = (inv) => {
    if (!window.confirm(`Revoke this invite${inv.label ? ` (“${inv.label}”)` : ""}? Links already used stay linked.`)) return;
    ppapiPost("agencyRevokeInvite", { inviteId: inv.inviteId })
      .then(() => { toast.success("Invite revoked"); load(); })
      .catch((e) => toast.error(apiErrorMessage(e, "Could not revoke the invite.")));
  };

  if (accountType !== "agency") {
    return (
      <div className="acWrap"><div className="acCard" style={{ maxWidth: 560 }}>
        <div className="acName">Agency tools</div>
        <p className="secLead">This section is for agency accounts. Reach out on Discord if you run an agency.</p>
        <Link to="/overview" className="btnGhost">Back to dashboard</Link>
      </div></div>
    );
  }

  const cols = "minmax(160px,1.2fr) minmax(220px,1.6fr) 90px 120px 110px 100px";
  const stateOf = (inv) => {
    if (inv.revokedAt) return ["revoked", "fail"];
    if (inv.expiresAt && new Date(inv.expiresAt) < new Date()) return ["expired", "fail"];
    if (inv.maxUses && Number(inv.uses) >= Number(inv.maxUses)) return ["used up", "pend"];
    return ["active", "done"];
  };

  return (
    <>
      <Helmet><title>Agency Invites | Instaswap Partners</title></Helmet>
      <div>
        <div className="secHdr">
          <h2 className="secTitle">Invites</h2>
          <p className="secLead">
            Share an invite link with a new KOL. Signing up through it creates a KOL account that is already linked to your
            agency — no approval step. Someone who already has an account will be asked to accept instead.
          </p>
        </div>

        <div className="panel" style={{ maxWidth: 900, marginBottom: 24, padding: 18 }}>
          <h3 className="secH" style={{ marginTop: 0, display: "flex", alignItems: "center", gap: 10 }}>
            <FontAwesomeIcon icon={faEnvelopeOpenText} /> New invite link
          </h3>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(200px,2fr) 120px 170px auto", gap: 10, alignItems: "end" }}>
            <div>
              <label className="fldLbl">Label <span style={{ color: "#7c847e", fontWeight: 400 }}>(optional, only you see it)</span></label>
              <input className="input" type="text" value={label} maxLength={60} placeholder="e.g. Twitter batch — October" onChange={(e) => setLabel(e.target.value)} />
            </div>
            <div>
              <label className="fldLbl">Max uses</label>
              <input className="input" type="number" min="1" value={maxUses} placeholder="∞" onChange={(e) => setMaxUses(e.target.value)} />
            </div>
            <div>
              <label className="fldLbl">Expires</label>
              <input className="input" type="date" value={expiresAt} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setExpiresAt(e.target.value)} />
            </div>
            <button type="button" className="btnPrimary" onClick={create} disabled={creating} style={{ height: 46 }}>
              {creating ? <FontAwesomeIcon icon={faSpinner} spin /> : <><FontAwesomeIcon icon={faPlus} /> Create</>}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="gtEmpty"><FontAwesomeIcon icon={faSpinner} spin /> Loading…</div>
        ) : invites.length === 0 ? (
          <div className="gtEmpty">No invites yet.</div>
        ) : (
          <div className="gtScroll">
            <div style={{ minWidth: 860 }}>
              <div className="gtHead" style={{ gridTemplateColumns: cols }}>
                <span>Label</span><span>Link</span><span className="r">Uses</span><span>Expires</span><span>Status</span><span className="r">Actions</span>
              </div>
              {invites.map((inv) => {
                const [st, pill] = stateOf(inv);
                const url = inviteUrl(inv.token);
                return (
                  <div className="gtRow" key={inv.inviteId} style={{ gridTemplateColumns: cols }}>
                    <span className="cellStrong">{inv.label || <span style={{ color: "#7c847e" }}>—</span>}<small style={{ display: "block", color: "#7c847e" }}>{inv.createdAt ? new Date(inv.createdAt).toLocaleDateString() : ""}</small></span>
                    <span className="cellMono" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 12 }} title={url}>{url}</span>
                    <span className="r">{Number(inv.uses) || 0}{inv.maxUses ? ` / ${inv.maxUses}` : ""}</span>
                    <span>{inv.expiresAt ? new Date(inv.expiresAt).toLocaleDateString() : "never"}</span>
                    <span><span className={`pill ${pill}`}><span className="pd" />{st}</span></span>
                    <span className="r" style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                      {st === "active" && <button type="button" className="iconBtn edit" title="Copy link" onClick={() => copy(url)}><FontAwesomeIcon icon={faCopy} /></button>}
                      {st === "active" && <button type="button" className="iconBtn danger" title="Revoke" onClick={() => revoke(inv)}><FontAwesomeIcon icon={faBan} /></button>}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AgencyInvites;
